// Pure serializers for the network-topology graph (B5 exports).
// Kept framework-free so they are unit-testable without a browser.

export interface GraphNode {
  id: string;
  label: string;
  role: string; // host | router | switch | subnet
  os: string; // windows | linux | macos | unknown
  hostname?: string;
  critical?: boolean;
}

export interface GraphEdge {
  id: string;
  from: string;
  to: string;
  port?: string;
  service?: string;
}

// Strict dotted-quad IPv4 test. A node whose `ip` is not an IPv4 address (an
// agent UUID, a hostname, an IPv6 address) must NOT be bucketed into a subnet:
// ip.split(".").slice(0,3).join(".") + ".0/24" turns a UUID into a nonsense
// parent node like "{C219CFA7-...}.0/24" instead of skipping it.
const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

export function isIPv4(ip: string): boolean {
  const m = IPV4_RE.exec(ip);
  return !!m && m.slice(1).every((o) => Number(o) <= 255);
}

// The /24 label an IPv4 belongs to; "" when the input is not IPv4.
export function subnetOf(ip: string): string {
  return isIPv4(ip) ? ip.split(".").slice(0, 3).join(".") + ".0/24" : "";
}

export function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export function buildGraphJSON(nodes: GraphNode[], edges: GraphEdge[]): string {
  return JSON.stringify({ nodes, edges }, null, 2);
}

export function buildGraphML(nodes: GraphNode[], edges: GraphEdge[]): string {
  const lines: string[] = [];
  lines.push('<?xml version="1.0" encoding="UTF-8"?>');
  lines.push('<graphml xmlns="http://graphml.graphdrawing.org/xmlns">');
  lines.push('  <key id="role" for="node" attr.name="role" attr.type="string"/>');
  lines.push('  <key id="os" for="node" attr.name="os" attr.type="string"/>');
  lines.push('  <key id="hostname" for="node" attr.name="hostname" attr.type="string"/>');
  lines.push('  <key id="critical" for="node" attr.name="critical" attr.type="boolean"/>');
  lines.push('  <key id="port" for="edge" attr.name="port" attr.type="string"/>');
  lines.push('  <key id="service" for="edge" attr.name="service" attr.type="string"/>');
  lines.push('  <graph id="G" edgedefault="directed">');
  for (const n of nodes) {
    const attrs = [
      `      <data key="role">${escapeXml(n.role)}</data>`,
      `      <data key="os">${escapeXml(n.os)}</data>`,
      n.hostname ? `      <data key="hostname">${escapeXml(n.hostname)}</data>` : "",
      n.critical ? `      <data key="critical">true</data>` : "",
    ]
      .filter(Boolean)
      .join("\n");
    lines.push(`    <node id="${escapeXml(n.id)}" label="${escapeXml(n.label)}">`);
    if (attrs) lines.push(attrs);
    lines.push('    </node>');
  }
  for (const e of edges) {
    const attrs = [
      e.port ? `      <data key="port">${escapeXml(e.port)}</data>` : "",
      e.service ? `      <data key="service">${escapeXml(e.service)}</data>` : "",
    ]
      .filter(Boolean)
      .join("\n");
    lines.push(
      `    <edge id="${escapeXml(e.id)}" source="${escapeXml(e.from)}" target="${escapeXml(e.to)}">`,
    );
    if (attrs) lines.push(attrs);
    lines.push('    </edge>');
  }
  lines.push('  </graph>');
  lines.push('</graphml>');
  return lines.join("\n");
}

// draw.io (mxGraph) XML. Positions are a simple grid layout — draw.io re-layouts
// on open anyway; the point is the node/edge structure + attributes.
export function buildDrawioXML(nodes: GraphNode[], edges: GraphEdge[]): string {
  const COLS = 6;
  const CELL_W = 160;
  const CELL_H = 100;
  const lines: string[] = [];
  lines.push('<mxfile host="app.diagrams.net">');
  lines.push('  <diagram name="network">');
  lines.push('    <mxGraphModel>');
  lines.push('      <root>');
  lines.push('        <mxCell id="0"/>');
  lines.push('        <mxCell id="1" parent="0"/>');
  const cellIds = new Map<string, string>();
  nodes.forEach((n, i) => {
    const cellId = `n${i}`;
    cellIds.set(n.id, cellId);
    const x = (i % COLS) * CELL_W;
    const y = Math.floor(i / COLS) * CELL_H;
    const shape = n.role === "subnet" ? "rhombus" : "ellipse";
    const label = escapeXml(`${n.label}${n.hostname ? ` (${n.hostname})` : ""}`);
    lines.push(
      `        <mxCell id="${cellId}" value="${label}" style="${shape}" vertex="1" parent="1">`,
    );
    lines.push(
      `          <mxGeometry x="${x}" y="${y}" width="80" height="40" as="geometry"/>`,
    );
    lines.push('        </mxCell>');
  });
  for (const e of edges) {
    const src = cellIds.get(e.from);
    const dst = cellIds.get(e.to);
    if (!src || !dst) continue;
    const label = escapeXml([e.port, e.service].filter(Boolean).join(" "));
    lines.push(
      `        <mxCell id="${escapeXml(e.id)}" value="${label}" edge="1" source="${src}" target="${dst}" parent="1"/>`,
    );
  }
  lines.push('      </root>');
  lines.push('    </mxGraphModel>');
  lines.push('  </diagram>');
  lines.push('</mxfile>');
  return lines.join("\n");
}

// CSV: node rows then edge rows in one file (first column = record type).
export function buildGraphCSV(nodes: GraphNode[], edges: GraphEdge[]): string {
  const csv = (v: string | undefined): string => {
    const s = v ?? "";
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines: string[] = ["type,id,label,role,os,hostname,critical,from,to,port,service"];
  for (const n of nodes) {
    lines.push(`node,${csv(n.id)},${csv(n.label)},${csv(n.role)},${csv(n.os)},${csv(n.hostname ?? "")},${n.critical ? "true" : "false"},,,,,`);
  }
  for (const e of edges) {
    lines.push(`edge,${csv(e.id)},,,,,,${csv(e.from)},${csv(e.to)},${csv(e.port ?? "")},${csv(e.service ?? "")}`);
  }
  return lines.join("\n");
}

export function downloadText(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
